import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useJobDetail, useRelatedJobs } from '@/hooks/useJobDetail';
import { useSavedJob } from '@/hooks/useSavedJob';
import { isRecruiterPosting, trackRecruiterJobInteraction } from '@/hooks/useJobInteractionTracking';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { JobPageSEO } from '@/components/JobPageSEO';
import { JobDetailV2Body } from '@/pages/jdv2/JobDetailV2Body';
import { buildJobPath } from '@/utils/jobSlug';
import {
  JobDetailV2ApplyModal,
  hasAlreadyCapturedEmail,
} from '@/pages/jdv2/JobDetailV2ApplyModal';
import '@/pages/jdv2/jdv2-styles.css';
import { JobAlertSignup } from '@/components/JobAlertSignup';
import { ALERT_PLACE } from '@/lib/alertPlacement';
import { JobPayLine } from '@/components/JobPayLine';
import { ClosedJobNotice } from '@/components/ClosedJobNotice';
import { siteEvents } from '@/lib/siteEvents';
import { employerName, employerPossessive, EMPLOYER_UNNAMED, UnnamedEmployerMark } from '@/lib/employer';
import { StillRemoteFact } from '@/components/StillRemoteFact';
import { NewsletterSignupCard } from '@/components/NewsletterSignupCard';
import CopilotNudge from '@/components/copilot/CopilotNudge';

/* ============================================================
 * PLACEHOLDER DATA
 * ============================================================
 * These fields aren't available from the current useJobDetail
 * hook. They are rendered with TODO placeholders so the layout
 * matches the prototype 1:1. Wire them in once the hook /
 * enrich-job-detail edge function ships.
 *
 *   - tldrBullets             (no source today)
 *   - eyebrowMarker / eyebrowMiddle  (no `funding_stage` in jobs)
 *   - techStackChips          (keywords[] is too noisy without an allow-list)
 *   - companyDescription      (jobs.company_description exists, hook omits it)
 *   - foundedYear             (jobs.founded_year exists, hook omits it)
 *   - companyStats            (employees / TPV / raised: no source today)
 *   - visaSponsorship         (no source today)
 *   - seniorityFact / functionFact  (jobs.seniority + .department exist, hook omits)
 * ============================================================ */
const PLACEHOLDER_EYEBROW_MARKER = 'Hiring now';
const PLACEHOLDER_EYEBROW_MIDDLE = 'Accounting + Finance';

/* Feature flags. Real data now flows through enriched_data; render gates
   below ensure these sections only show when the AI returned usable
   payloads (4 TL;DR bullets, >=1 tech chip). */
const SHOW_TLDR_CARD = true;
const SHOW_TECH_STACK = true;

/* Helpers */
function postedAgo(date: Date | null | undefined): string | null {
  if (!date) return null;
  const ms = Date.now() - date.getTime();
  if (ms < 0) return null;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  if (days <= 60) return `${days} days ago`;
  return null; // hide over 60 days, matches the homepage
}

function isNew(date: Date | null | undefined): boolean {
  if (!date) return false;
  const ms = Date.now() - date.getTime();
  return ms < 7 * 24 * 3_600_000;
}

function companyInitials(name: string): string {
  if (!name || !name.trim()) return '';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || name[0]!.toUpperCase();
}

export default function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const detail = useJobDetail(id);
  const { data: job, isLoading, error } = detail;
  const { data: relatedJobs = [], isLoading: relatedLoading } = useRelatedJobs(
    job?.id,
    job?.title
  );
  const [modalOpen, setModalOpen] = useState(false);
  // Hooks must precede the loading/not-found early returns below — a hook
  // declared after them appears only on some renders and crashes the page.
  const [flagState, setFlagState] = useState<'idle' | 'busy' | 'done'>('idle');
  const handleFlagExpired = async () => {
    if (!job?.id || flagState !== 'idle') return;
    setFlagState('busy');
    try {
      await (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => PromiseLike<{ error: unknown }>)(
        'flag_job', { p_job_id: job.id, p_reason: 'expired' },
      );
      setFlagState('done');
      toast.success('Flagged — our checker will visit the posting within the hour.');
    } catch {
      setFlagState('idle');
      toast.error('Could not flag this job — try again.');
    }
  };
  // Ghost reports are a different loop from the link patrol: they are about
  // the EMPLOYER, not the posting, so they require a signed-in reporter and
  // publish only in aggregate once several independent reporters agree.
  const [ghostState, setGhostState] = useState<'idle' | 'busy' | 'done'>('idle');
  const handleReportGhosted = async () => {
    if (!job?.id || ghostState !== 'idle') return;
    setGhostState('busy');
    try {
      const { data } = await (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: { ok: boolean; reason?: string } | null; error: unknown }>)(
        'flag_job', { p_job_id: job.id, p_reason: 'ghosted' },
      );
      if (data && data.ok === false && data.reason === 'auth_required') {
        setGhostState('idle');
        toast.error('Sign in to report ghosting — reports only count when they come from real accounts.');
        return;
      }
      setGhostState('done');
      toast.success('Recorded. Ghost reports publish only when several candidates independently confirm.');
    } catch {
      setGhostState('idle');
      toast.error('Could not record the report — try again.');
    }
  };
  // Hooks must run on every render — call here, not after early returns.
  const { isSaved, toggle: toggleSaved, canSave } = useSavedJob(job?.id);
  const { session } = useAuth();
  // Paid recruiter postings report views and apply clicks back to the recruiter
  // dashboard (views_count, applications_count). Board-scraped jobs have no
  // posting row behind them, so they are skipped.
  const recruiterPosting = isRecruiterPosting(job);
  useEffect(() => {
    if (job?.id && recruiterPosting) void trackRecruiterJobInteraction(job.id, 'view');
  }, [job?.id, recruiterPosting]);

  // 301-equivalent: if we landed on a legacy UUID URL, replace to the canonical slug.
  useEffect(() => {
    if (job && (detail as any).isLegacyUuid) {
      navigate(buildJobPath(job), { replace: true });
    }
  }, [job, detail, navigate]);

  /* Loading skeleton */
  if (isLoading) {
    return (
      <div className="jdv2-root">
        <section className="job-header">
          <div className="container">
            <div className="breadcrumb">
              <span style={{ color: 'var(--af-grey-300)' }}>Loading job…</span>
            </div>
            <div className="job-header__top">
              <div
                className="job-header__logo"
                style={{ background: 'var(--af-grey-100)', boxShadow: 'none' }}
                aria-hidden="true"
              />
              <div>
                <div
                  style={{
                    height: 18,
                    width: 160,
                    background: 'var(--af-grey-100)',
                    borderRadius: 6,
                    marginBottom: 14,
                  }}
                />
                <div
                  style={{
                    height: 36,
                    width: '70%',
                    background: 'var(--af-grey-100)',
                    borderRadius: 8,
                    marginBottom: 14,
                  }}
                />
                <div
                  style={{
                    height: 16,
                    width: 280,
                    background: 'var(--af-grey-100)',
                    borderRadius: 6,
                  }}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  /* Error / not found */
  if (error || !job) {
    const popularLandings = [
      { path: '/remote-accounting-jobs', label: 'Remote Accounting Jobs' },
      { path: '/remote-cpa-jobs', label: 'Remote CPA Jobs' },
      { path: '/remote-tax-jobs', label: 'Remote Tax Jobs' },
      { path: '/remote-bookkeeper-jobs', label: 'Remote Bookkeeper Jobs' },
      { path: '/remote-controller-jobs', label: 'Remote Controller Jobs' },
    ];
    return (
      <div className="jdv2-root">
        <Helmet>
          <title>Job not found | AuditFriendly</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <div className="jdv2-error-shell">
          <div className="jdv2-error-card" style={{ maxWidth: 640 }}>
            <h1>This job is no longer available</h1>
            <p>
              The posting you tried to open has been removed or expired. We
              take jobs down after 30 days to keep results fresh, and thousands
              of accounting and finance roles are open right now.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 16, marginBottom: 24 }}>
              <Link to="/search" className="btn btn--primary btn--small">
                Search all jobs
              </Link>
              <Link to="/" className="btn btn--secondary btn--small">
                Browse the job board
              </Link>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Popular searches</h2>
            <ul style={{ listStyle: 'disc', paddingLeft: 20, lineHeight: 1.8 }}>
              {popularLandings.map((lp) => (
                <li key={lp.path}>
                  <Link to={lp.path}>{lp.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }


  /* Real data extraction */
  const isRemote = Boolean(job.is_remote ?? job.isRemote);
  const isClosed = Boolean(job.is_archived);
  // '' when the listing names no employer (see src/lib/employer.tsx).
  const employer = employerName(job.company);
  const closedAt = (job as { archived_at?: string | null }).archived_at ?? null;
  // Some employers name a site ("Headquarters") or only a country. The
  // location then stops at the state or the country, and the page says a city
  // was not given instead of leaving the reader to wonder.
  const cityUnlisted = ['state_only', 'country_only'].includes(
    (job as { location_granularity?: string | null }).location_granularity ?? '');
  const applyLabel = isClosed ? 'View the original posting →' : 'Apply for this role →';
  // What an alert from this page is for: the job's role, place and hours.
  const alertJob = {
    id: job.id,
    title: job.title,
    location: job.location ?? null,
    is_remote: isRemote,
    canonical_role: (job as { canonical_role?: string | null }).canonical_role ?? null,
    hours: job.hours ?? null,
  };
  const isFeatured = Boolean(job.is_featured ?? job.isFeatured);
  const applyLink = job.apply_link ?? job.applyLink ?? '';
  const postDate = job.post_date ? new Date(job.post_date) : null;
  const postedLabel = postedAgo(postDate);
  const newBadge = isNew(postDate);
  const salaryDisplay =
    (job as { display_salary?: string }).display_salary ||
    job.salary ||
    null;

  // Real fields from useJobDetail
  const companyDescription = job.company_description ?? null;
  const foundedYear = job.founded_year ?? null;

  const markPendingApplication = () => {
    try {
      sessionStorage.setItem(
        'pendingJobConfirmation',
        JSON.stringify({
          jobId: job.id,
          jobTitle: job.title,
          company: job.company,
          openedAt: Date.now(),
        })
      );
    } catch {
      /* ignore storage errors */
    }
  };

  // Paid recruiter posts can carry a mailto: apply target. window.open on one
  // leaves a blank tab and, on some browsers, never reaches the mail client.
  const openApplyLink = (link: string) => {
    if (link.startsWith('mailto:')) {
      window.location.href = link;
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const handleApplyClick = () => {
    if (!applyLink) return;
    siteEvents.convert('apply', { job_id: job.id, closed: isClosed || undefined });
    if (recruiterPosting) void trackRecruiterJobInteraction(job.id, 'click');
    markPendingApplication();
    // Signed-in users skip the email soft-capture (they're already a known user)
    if (session || hasAlreadyCapturedEmail()) {
      openApplyLink(applyLink);
    } else {
      setModalOpen(true);
    }
  };

  const redirectToApply = () => {
    if (applyLink) {
      markPendingApplication();
      openApplyLink(applyLink);
    }
  };

  const jobCaptureContext = {
    id: job.id,
    title: job.title,
    company: job.company,
    location: job.location,
    isRemote,
  };

  return (
    <>
      <JobPageSEO jobId={id!} />

      {/* jd-alert-place-inline: split test alert_place_v1 (JobAlertSignup). */}
      <div className={`jdv2-root${ALERT_PLACE === 'inline' ? ' jd-alert-place-inline' : ''}`}>
        {/* ====================== JOB HEADER ====================== */}
        <section className="job-header">
          <div className="container">
            <div className="breadcrumb">
              <Link to="/">Jobs</Link>
              <span className="breadcrumb__sep">/</span>
              <span className="breadcrumb__current">{job.title}</span>
            </div>

            <div className="job-header__top">
              <div className={`job-header__logo${employer ? '' : ' job-header__logo--unnamed'}`}>
                {!employer ? (
                  <UnnamedEmployerMark size={26} />
                ) : job.logo ? (
                  <img
                    src={job.logo}
                    alt={`${employer} logo`}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) parent.textContent = companyInitials(job.company);
                    }}
                  />
                ) : (
                  companyInitials(job.company)
                )}
              </div>
              <div>
                <span className="job-eyebrow">
                  {/* TODO: derive marker word from a real signal (funding stage / seniority) */}
                  <span className="marker">{isClosed ? 'Closed' : PLACEHOLDER_EYEBROW_MARKER}</span>
                  <span>·</span>
                  {/* TODO: derive from canonical_role or department */}
                  <span>{PLACEHOLDER_EYEBROW_MIDDLE}</span>
                </span>

                <h1>{job.title}</h1>

                <div className="job-header__company">
                  {employer ? (
                    <span className="company-name">{employer}</span>
                  ) : (
                    <span className="company-name company-name--unnamed">{EMPLOYER_UNNAMED}</span>
                  )}
                  {job.location && (
                    <>
                      <span className="dot" />
                      <span>{job.location}</span>
                    </>
                  )}
                  {job.hours && (
                    <>
                      <span className="dot" />
                      <span>{job.hours}</span>
                    </>
                  )}
                  {postedLabel && (
                    <>
                      <span className="dot" />
                      <span>Posted {postedLabel}</span>
                    </>
                  )}
                </div>

                <div className="job-header__badges">
                  {isFeatured && (
                    <span className="badge badge--featured">★ Featured</span>
                  )}
                  {newBadge && <span className="badge badge--new">New</span>}
                  {isRemote && <span className="badge badge--remote">Remote</span>}
                  {/* Hybrid badge intentionally omitted: jobs.is_remote is a
                      boolean today, no third state. Add when work_arrangement
                      enum lands. */}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ====================== MAIN CONTENT ====================== */}
        <section className="job-body">
          <div className="container">
            <div className="job-body__grid">
              {/* LEFT */}
              <div className="job-content">
                {isClosed && <ClosedJobNotice jobId={job.id} title={job.title} closedAt={closedAt} alertJob={alertJob} />}

                {/* TL;DR  ---  populated by enrich-job-detail. Renders only
                    when the AI returned exactly 4 bullets; otherwise hidden
                    (no skeleton, no empty card). */}
                {SHOW_TLDR_CARD && job.enriched_data?.tldr_bullets?.length === 4 && (
                  <div className="tldr">
                    <div className="tldr__title">
                      <svg
                        className="icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.4}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4" />
                        <path d="M12 8h.01" />
                      </svg>
                      The 30-second version
                    </div>
                    <ul className="tldr__list">
                      {job.enriched_data.tldr_bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Email signup for phones, where the sidebar falls below the
                    whole description, and for desktop visits in arm `inline` of
                    alert_place_v1. This slot held "What the data says about
                    this job" until 2026-10-06 (316 sightings, 4 clicks in ten
                    days); its pay comparison now sits under the salary, and
                    crawlers still get every fact through seo_for_job(). On a
                    closed posting the signup lives inside ClosedJobNotice. */}
                {!isClosed && <JobAlertSignup job={alertJob} variant="inline" />}

                {/* About / role content
                    Currently a single rich-text block from format-job-content.
                    Round 2 will split into About / Day-to-day / Bring / Nice-to-have. */}
                <section>
                  <span className="section-eyebrow">About the role</span>
                  <h2 className="section-title">What you need to know</h2>
                  <JobDetailV2Body
                    rawContent={job.jobFullText ?? job.description}
                    formattedContent={job.formattedJobContent}
                    jobId={job.id}
                  />
                </section>

                {/* Tech stack  ---  populated by enrich-job-detail. Hidden
                    when no chips were extracted. */}
                {SHOW_TECH_STACK && (job.enriched_data?.tech_chips?.length ?? 0) > 0 && (
                  <section>
                    <span className="section-eyebrow">Tools you'll work with</span>
                    <h2 className="section-title">Tech stack</h2>
                    <div className="tech-stack">
                      {job.enriched_data!.tech_chips!.map((chip) => (
                        <span key={chip} className="tech-chip">
                          {chip}
                        </span>
                      ))}
                    </div>
                  </section>
                )}

                {/* Company card */}
                <section>
                  <span className="section-eyebrow">About the company</span>
                  <div className="company-card">
                    <div className="company-card__top">
                      <div className={`company-card__logo${employer ? '' : ' company-card__logo--unnamed'}`}>
                        {!employer ? (
                          <UnnamedEmployerMark size={24} />
                        ) : job.logo ? (
                          <img
                            src={job.logo}
                            alt={`${employer} logo`}
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display =
                                'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent)
                                parent.textContent = companyInitials(job.company);
                            }}
                          />
                        ) : (
                          companyInitials(job.company)
                        )}
                      </div>
                      <div>
                        <div className="company-card__name">{employer ?? EMPLOYER_UNNAMED}</div>
                        <div className="company-card__tag">
                          {/* TODO: prepend funding/category once company_intelligence is populated */}
                          {!employer ? 'Not named in the listing' : foundedYear ? `Founded ${foundedYear}` : 'Hiring company'}
                        </div>
                      </div>
                    </div>
                    {!employer && (
                      <p className="company-card__desc">
                        This listing does not say who the employer is. Recruiting agencies often post for clients
                        this way, and name the employer once you are in touch.
                      </p>
                    )}
                    {employer && companyDescription && (
                      <p className="company-card__desc">{companyDescription}</p>
                    )}
                    {/* Still Remote: renders only for employers listed on stillremote.com. */}
                    {employer && <StillRemoteFact company={employer} />}
                    {/* Sourced stats only. Founded reads from job.founded_year;
                        Employees and Funding come back when a company_intelligence
                        source ships. With nothing sourced the row is hidden
                        instead of showing placeholder dashes. */}
                    {foundedYear != null && (
                      <div className="company-card__stats">
                        <div>
                          <div className="company-stat__num">{foundedYear}</div>
                          <div className="company-stat__label">Founded</div>
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                {/* Bottom apply CTA */}
                <section style={{ marginTop: 56, textAlign: 'center' }}>
                  <h2 className="section-title" style={{ marginBottom: 12 }}>
                    Ready to apply?
                  </h2>
                  <p
                    style={{
                      color: 'var(--af-ink-600)',
                      marginBottom: 24,
                      fontSize: 15,
                    }}
                  >
                    You'll be redirected to {employerPossessive(job.company)} application page.
                  </p>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={handleApplyClick}
                    disabled={!applyLink}
                  >
                    {applyLabel}
                  </button>
                  {/* Community patrol: a flagged job jumps the hourly link-check
                      queue; if the employer's page is dead we take it down and
                      say so on /data/curation. */}
                  <div style={{ marginTop: 14 }}>
                    <button
                      type="button"
                      onClick={handleFlagExpired}
                      disabled={flagState !== 'idle'}
                      style={{
                        background: 'none', border: 'none', cursor: flagState === 'idle' ? 'pointer' : 'default',
                        fontSize: 13, fontWeight: 600, color: 'var(--af-ink-600)',
                        textDecoration: flagState === 'idle' ? 'underline' : 'none', padding: 0,
                        fontFamily: 'inherit',
                      }}
                    >
                      {flagState === 'done'
                        ? 'Thanks — we\u2019ll verify this posting within the hour.'
                        : 'Job expired or link broken? Flag it'}
                    </button>
                  </div>
                  {/* Ghosting is the complaint every candidate has and no board
                      measures. Signed-in only; one report per reporter per
                      company; nothing publishes below three independent
                      reporters — the layoff tracker's gate, applied to people. */}
                  <div style={{ marginTop: 6 }}>
                    <button
                      type="button"
                      onClick={handleReportGhosted}
                      disabled={ghostState !== 'idle'}
                      style={{
                        background: 'none', border: 'none', cursor: ghostState === 'idle' ? 'pointer' : 'default',
                        fontSize: 13, fontWeight: 600, color: 'var(--af-ink-600)',
                        textDecoration: ghostState === 'idle' ? 'underline' : 'none', padding: 0,
                        fontFamily: 'inherit',
                      }}
                    >
                      {ghostState === 'done'
                        ? 'Recorded. Thank you.'
                        : 'Interviewed here and never heard back? Report ghosting'}
                    </button>
                  </div>
                </section>
              </div>

              {/* RIGHT */}
              <aside className="rail">
                {/* Salary card with primary CTA */}
                <div className="rail-card salary-card">
                  <div className="salary-card__label">
                    {salaryDisplay ? 'Total compensation' : 'Apply'}
                  </div>
                  {salaryDisplay ? (
                    <>
                      <div className="salary-card__amount">{salaryDisplay}</div>
                      <JobPayLine jobId={job.id} />
                    </>
                  ) : (
                    <div className="salary-card__amount">{job.title}</div>
                  )}
                  <div className="salary-card__sub">
                    {salaryDisplay
                      ? 'Compensation as posted by the hiring team.'
                      : `Apply directly through ${employerPossessive(job.company)} page.`}
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={handleApplyClick}
                    disabled={!applyLink}
                  >
                    {applyLabel}
                  </button>
                  <div className="rail__actions">
                    <button
                      type="button"
                      className="rail-icon-btn"
                      aria-pressed={isSaved}
                      disabled={!canSave}
                      onClick={() => {
                        if (!canSave) {
                          toast.error('Sign in to save jobs');
                          return;
                        }
                        const next = !isSaved;
                        toggleSaved();
                        toast.success(next ? 'Saved to your jobs' : 'Removed from saved');
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill={isSaved ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        strokeWidth={2.2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z" />
                      </svg>
                      {isSaved ? 'Saved' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="rail-icon-btn"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(window.location.href);
                        } catch {
                          /* ignore */
                        }
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                        <polyline points="16 6 12 2 8 6" />
                        <line x1="12" y1="2" x2="12" y2="15" />
                      </svg>
                      Share
                    </button>
                  </div>
                </div>

                {!isClosed && <JobAlertSignup job={alertJob} variant="rail" />}

                {/* Quick facts */}
                <div className="rail-card facts">
                  <h4>Quick facts</h4>
                  <dl>
                    <div className="facts__row">
                      <span className="facts__label">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect width="20" height="14" x="2" y="6" rx="2" />
                          <path d="M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                        </svg>
                        Employment
                      </span>
                      <span className="facts__value">{job.hours || 'Full-Time'}</span>
                    </div>
                    {job.location && (
                      <div className="facts__row">
                        <span className="facts__label">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 10c0 7-8 12-8 12s-8-5-8-12a8 8 0 0 1 16 0Z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          Location
                        </span>
                        <span className="facts__value">
                          {job.location}
                          {cityUnlisted && !isRemote && <span className="facts__note">City not listed</span>}
                        </span>
                      </div>
                    )}
                    {job.seniority && (
                      <div className="facts__row">
                        <span className="facts__label">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                          </svg>
                          Seniority
                        </span>
                        <span className="facts__value">{job.seniority}</span>
                      </div>
                    )}
                    {job.department && (
                      <div className="facts__row">
                        <span className="facts__label">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 2v4" />
                            <path d="M12 18v4" />
                            <path d="m4.93 4.93 2.83 2.83" />
                            <path d="m16.24 16.24 2.83 2.83" />
                            <path d="M2 12h4" />
                            <path d="M18 12h4" />
                            <path d="m4.93 19.07 2.83-2.83" />
                            <path d="m16.24 7.76 2.83-2.83" />
                          </svg>
                          Function
                        </span>
                        <span className="facts__value">{job.department}</span>
                      </div>
                    )}
                  </dl>
                </div>

                {/* Free Remote Accountant signup. Until 2026-11-03 this slot ran
                    split test job_rail_offer_v3 (Still Remote pass vs Search
                    Sprint): about 2,700 views and 0 clicks for both arms in the
                    7 days to Nov 3. Selling now happens in the newsletter's
                    welcome series, so the slot builds the list. Same in both
                    arms of alert_place_v1, so it does not skew that test. */}
                <NewsletterSignupCard surface="job_rail" />
              </aside>
            </div>
          </div>
        </section>

        {/* ====================== SIMILAR ROLES ====================== */}
        {(relatedLoading || relatedJobs.length > 0) && (
          <section className="similar">
            <div className="container">
              <div className="similar__head">
                <h2>Similar roles you might like</h2>
                <Link to="/" className="similar__see-all">
                  See all →
                </Link>
              </div>
              <div className="similar__grid">
                {relatedLoading
                  ? Array.from({ length: 3 }).map((_, i) => (
                      <div
                        key={i}
                        className="similar-card"
                        style={{ pointerEvents: 'none', opacity: 0.6 }}
                        aria-hidden="true"
                      >
                        <div className="similar-card__top">
                          <div
                            className="similar-card__logo"
                            style={{ background: 'var(--af-grey-100)' }}
                          />
                          <div>
                            <div
                              className="similar-card__company"
                              style={{
                                width: 80,
                                height: 12,
                                background: 'var(--af-grey-100)',
                                borderRadius: 4,
                              }}
                            />
                          </div>
                        </div>
                        <h3>&nbsp;</h3>
                      </div>
                    ))
                  : relatedJobs.slice(0, 3).map((rel) => (
                      <Link
                        key={rel.id}
                        to={buildJobPath(rel)}
                        className="similar-card"
                      >
                        <div className="similar-card__top">
                          <div
                            className="similar-card__logo"
                            style={employerName(rel.company) ? { background: 'var(--af-navy)' } : { background: 'var(--af-grey-100)', color: 'var(--af-ink-600)' }}
                          >
                            {!employerName(rel.company) ? (
                              <UnnamedEmployerMark size={18} />
                            ) : rel.logo ? (
                              <img
                                src={rel.logo}
                                alt={`${employerName(rel.company) ?? 'Employer'} logo`}
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).style.display =
                                    'none';
                                  const parent = e.currentTarget.parentElement;
                                  if (parent)
                                    parent.textContent = companyInitials(rel.company);
                                }}
                              />
                            ) : (
                              companyInitials(rel.company)
                            )}
                          </div>
                          <div>
                            <div className="similar-card__company">{employerName(rel.company) ?? EMPLOYER_UNNAMED}</div>
                          </div>
                        </div>
                        <h3>{rel.title}</h3>
                        <div className="similar-card__meta">
                          {rel.location && <span>{rel.location}</span>}
                          {(rel as { display_salary?: string }).display_salary ||
                          rel.salary ? (
                            <>
                              <span>·</span>
                              <span>
                                <strong>
                                  {(rel as { display_salary?: string }).display_salary ||
                                    rel.salary}
                                </strong>
                              </span>
                            </>
                          ) : null}
                        </div>
                      </Link>
                    ))}
              </div>
            </div>
          </section>
        )}

        {/* ====================== STICKY MOBILE APPLY ====================== */}
        <div className="sticky-apply">
          <div className="sticky-apply__info">
            <div className="sticky-apply__amount">
              {isClosed ? 'This posting closed' : salaryDisplay || 'Apply for this role'}
            </div>
            <div className="sticky-apply__sub">{job.title}</div>
          </div>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleApplyClick}
            disabled={!applyLink}
          >
            {isClosed ? 'Original posting →' : 'Apply →'}
          </button>
        </div>

        {/* ====================== APPLY MODAL ====================== */}
        <JobDetailV2ApplyModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onContinue={redirectToApply}
          jobContext={jobCaptureContext}
        />

        {/* Co-pilot corner nudge: desktop only (phones have the fixed Apply
            bar), after 30s, held back while the apply modal is open. */}
        <CopilotNudge source="job" paused={modalOpen} dwellMs={30_000} desktopOnly context={{ title: job.title.length <= 28 ? job.title : undefined }} />
      </div>
    </>
  );
}
